import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ChevronDownIcon } from '@rsos/assets/icons';
import { Box, Checkbox } from '@rsos/base-components';
import { useFlag } from '@rsos/flags';
import {
  CustomAccordionWrapper,
  CustomAccordionHeader,
  CustomAccordionContent,
  CaretWrapper,
} from './LayerCheckbox.styles';
import {
  GISSupplementalLayer,
  Sublayer,
  GISService,
  RootState,
} from './LayerCheckbox.types';

interface BasemapLayerCheckboxProps {
  layer: GISSupplementalLayer;
  layerID: number | string;
  isDisabled: boolean;
  activeGISServices: GISService[];
  handleGISCheckboxOnChange: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  handleSublayerCheckboxOnChange: (
    sublayer: Sublayer,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const BasemapLayerCheckbox: React.FC<BasemapLayerCheckboxProps> = ({
  layer,
  layerID,
  isDisabled,
  activeGISServices,
  handleGISCheckboxOnChange,
  handleSublayerCheckboxOnChange,
}) => {
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);

  const activeBasemapServiceSublayers = useSelector(
    (state: RootState) => state.gisServices.activeBasemapServiceSublayers,
  );

  const toggleAccordion = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsAccordionExpanded(!isAccordionExpanded);
    },
    [isAccordionExpanded],
  );

  const handleCheckboxClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
  }, []);

  const isMainLayerChecked = !!activeGISServices.find(
    (service: GISService) => service.id === layerID,
  );

  const hasBasemapSublayer = useFlag('basemap_sublayer');

  if (!hasBasemapSublayer) {
    return null;
  }
  return (
    <CustomAccordionWrapper>
      <CustomAccordionHeader
        isExpanded={isAccordionExpanded}
        onClick={toggleAccordion}
      >
        <Box onClick={handleCheckboxClick} p={0}>
          <Checkbox
            label={layer.display_name}
            isChecked={isMainLayerChecked}
            disabled={isDisabled}
            onChange={handleGISCheckboxOnChange}
          />
        </Box>
        <CaretWrapper isExpanded={isAccordionExpanded}>
          <ChevronDownIcon color="secondaryText" width={10} height={10} />
        </CaretWrapper>
      </CustomAccordionHeader>

      {isAccordionExpanded && (
        <CustomAccordionContent>
          {layer.sublayers?.map((sublayer, index) => (
            <Box key={sublayer.sublayerID || sublayer.id || index} p={0}>
              <Checkbox
                label={
                  sublayer.sublayerName ||
                  sublayer.title ||
                  `Sublayer ${index + 1}`
                }
                isChecked={
                  !!activeBasemapServiceSublayers.find(
                    activeSublayer =>
                      activeSublayer.sublayerID === sublayer.sublayerID,
                  )
                }
                disabled={isDisabled}
                onChange={handleSublayerCheckboxOnChange(sublayer)}
              />
            </Box>
          ))}
        </CustomAccordionContent>
      )}
    </CustomAccordionWrapper>
  );
};

export default BasemapLayerCheckbox;
